
import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PageLoader } from '@/components/ui/page-loader';

// Create a context to manage and share the loading state
interface AppLoadingContextType {
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const AppLoadingContext = createContext<AppLoadingContextType | null>(null);

// Custom hook to access the loading state
export const useAppLoading = (): AppLoadingContextType => {
  const context = useContext(AppLoadingContext);
  
  if (!context) {
    throw new Error("useAppLoading must be used within an AppLoadingProvider");
  }
  
  return context;
};

interface AppLoadingProviderProps {
  children: React.ReactNode;
}

export const AppLoadingProvider = ({ children }: AppLoadingProviderProps) => {
  // We'll use a simple approach here that doesn't depend on the router
  const [isLoading, setIsLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(false);
  const loadingTimerRef = useRef<number | null>(null);
  
  useEffect(() => {
    if (isLoading) {
      // Seulement afficher le loader après un délai de 300ms
      loadingTimerRef.current = window.setTimeout(() => {
        setShowLoader(true);
      }, 300);
    } else {
      // Cacher le loader immédiatement quand le chargement est terminé
      setShowLoader(false);
      
      // Nettoyer le timer si le chargement se termine avant 1 seconde
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }
    
    return () => {
      // Nettoyer le timer lorsque le composant est démonté ou l'état change
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    };
  }, [isLoading]);
  
  useEffect(() => {
    // Set loading to false after a short delay to allow the initial render
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);
    
    // Function to detect when the page has finished loading
    const handlePageLoad = () => {
      if (document.readyState === 'complete') {
        setIsLoading(false);
      }
    };
    
    // Listen for page load events
    window.addEventListener('load', handlePageLoad);
    document.addEventListener('readystatechange', handlePageLoad);
    
    // If the document is already loaded, update state
    if (document.readyState === 'complete') {
      setIsLoading(false);
    }

    // Navigation detection for SPA
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function() {
      const result = originalPushState.apply(this, arguments as any);
      // Set loading briefly during navigation
      setIsLoading(true);
      setTimeout(() => setIsLoading(false), 50);
      return result;
    };
    
    history.replaceState = function() {
      const result = originalReplaceState.apply(this, arguments as any);
      // Only set loading for actual navigation changes
      if (arguments[2] !== location.href) {
        setIsLoading(true);
        setTimeout(() => setIsLoading(false), 50);
      }
      return result;
    };
    
    // Handle browser back/forward
    const handlePopState = () => {
      setIsLoading(true);
      setTimeout(() => setIsLoading(false), 50);
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('load', handlePageLoad);
      document.removeEventListener('readystatechange', handlePageLoad);
      window.removeEventListener('popstate', handlePopState);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  return (
    <AppLoadingContext.Provider value={{ isLoading, setIsLoading }}>
      <AnimatePresence mode="wait">
        {showLoader && (
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <PageLoader />
          </motion.div>
        )}
      </AnimatePresence>
      {children}
    </AppLoadingContext.Provider>
  );
};
